import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { LogOut, Trash2, Plus, Pencil, X, Mail, Phone, FileText, ExternalLink, Settings as SettingsIcon, Save, Upload, AlertTriangle, Clock, CheckCircle2, MessageCircle, Zap, Image as ImageIcon, LayoutDashboard, FolderKanban, Newspaper, Images, Tags, MapPin as MapPinIcon, Menu, ClipboardList, ChevronDown, RefreshCw } from "lucide-react";
import api, { mediaUrl } from "../lib/api";
import { useLang } from "../context/LanguageContext";
import { CATEGORIES, EMIRATES, STATUSES } from "../lib/i18n";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "../components/ui/tabs";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../components/ui/dialog";

const ADMIN_SECTIONS = [
  { value: "dashboard", label: "Dashboard", description: "Overview", icon: LayoutDashboard },
  { value: "projects", label: "Projects", description: "Portfolio", icon: FolderKanban },
  { value: "quotations", label: "Quotations", description: "Customer requests", icon: ClipboardList },
  { value: "blog", label: "Blog content", description: "News & articles", icon: Newspaper },
  { value: "homepage", label: "Page images", description: "All public pages", icon: Images },
  { value: "media", label: "Media library", description: "Uploads", icon: ImageIcon },
  { value: "brands", label: "Brands", description: "Partners", icon: Tags },
  { value: "branches", label: "Branches", description: "Locations", icon: MapPinIcon },
  { value: "settings", label: "Settings", description: "Site tools", icon: SettingsIcon },
];

export default function AdminDashboard() {
  const { t } = useLang();
  const nav = useNavigate();
  const [activeTab, setActiveTab] = useState("dashboard");
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [refreshKey, setRefreshKey] = useState(0);
  const currentSection = ADMIN_SECTIONS.find((section) => section.value === activeTab) || ADMIN_SECTIONS[0];
  const adminEmail = localStorage.getItem("mk_admin_email") || "admin@mariotkitchen.com";
  const initials = adminEmail.slice(0, 2).toUpperCase();

  useEffect(() => {
    if (!localStorage.getItem("mk_token")) nav("/admin/login");
  }, [nav]);

  const logout = () => {
    localStorage.removeItem("mk_token");
    localStorage.removeItem("mk_admin_email");
    nav("/admin/login");
  };

  const refreshData = () => {
    setRefreshKey((value) => value + 1);
    toast.success("Dashboard data refreshed");
  };

  return (
    <div className="min-h-screen bg-[#f3f6fb] text-slate-900" data-testid="admin-dashboard">
      <Tabs value={activeTab} onValueChange={(value) => { setActiveTab(value); setSidebarOpen(false); }} className="min-h-screen">
        {sidebarOpen && <button aria-label="Close menu" className="fixed inset-0 z-40 bg-slate-950/40 lg:hidden" onClick={() => setSidebarOpen(false)} />}
        <aside className={`fixed inset-y-0 start-0 z-50 flex w-72 flex-col bg-[#101a2e] text-white transition-transform duration-300 lg:translate-x-0 ${sidebarOpen ? "translate-x-0" : "-translate-x-full"}`}>
          <div className="border-b border-white/10 px-7 py-8">
            <div className="flex items-center gap-4">
              <div className="flex h-14 w-14 shrink-0 items-center justify-center rounded-2xl bg-gradient-to-br from-[#0891b2] to-[#2563eb] font-display text-xl shadow-lg">{initials}</div>
              <div className="min-w-0"><p className="truncate font-display text-lg">Mariot Admin</p><p className="truncate text-sm text-slate-400">Content manager</p></div>
            </div>
          </div>
          <div className="px-4 pt-7"><p className="px-3 text-[10px] font-semibold tracking-[0.24em] text-slate-500">CONTENT MANAGEMENT</p></div>
          <TabsList className="mt-3 flex h-auto w-full flex-col items-stretch gap-1 bg-transparent px-3">
            {ADMIN_SECTIONS.map((section) => <Tab key={section.value} val={section.value} testid={`tab-${section.value}`} icon={section.icon}>{section.label}</Tab>)}
          </TabsList>
          <div className="mt-auto border-t border-white/10 p-4">
            <button onClick={logout} data-testid="admin-logout" className="flex w-full items-center gap-3 rounded-xl bg-rose-500/10 px-4 py-3 text-sm font-semibold text-rose-300 transition-colors hover:bg-rose-500/20"><LogOut className="h-4 w-4" /> {t.admin.logout}</button>
          </div>
        </aside>

        <div className="min-h-screen lg:ps-72">
          <header className="sticky top-0 z-30 flex h-[72px] items-center justify-between border-b border-slate-200 bg-white/95 px-5 backdrop-blur lg:px-8">
            <div className="flex items-center gap-3"><button aria-label="Open menu" onClick={() => setSidebarOpen(true)} className="rounded-lg p-2 text-slate-600 hover:bg-slate-100 lg:hidden"><Menu className="h-5 w-5" /></button><div><p className="text-xs font-medium text-slate-400">Mariot Kitchen</p><p className="font-semibold text-slate-700">Content Dashboard</p></div></div>
            <div className="flex items-center gap-3"><Button onClick={refreshData} variant="outline" size="sm" className="hidden border-slate-200 bg-white text-slate-700 hover:bg-slate-50 sm:inline-flex"><RefreshCw className="me-2 h-3.5 w-3.5" />Refresh data</Button><button aria-label="Refresh dashboard data" onClick={refreshData} className="rounded-lg p-2 text-slate-600 hover:bg-slate-100 sm:hidden"><RefreshCw className="h-5 w-5" /></button><div className="hidden text-end sm:block"><p className="max-w-[180px] truncate text-sm font-semibold text-slate-700">{adminEmail}</p><p className="text-xs text-slate-400">Administrator</p></div><div className="flex h-10 w-10 items-center justify-center rounded-full bg-[#0891b2] text-sm font-bold text-white">{initials}</div></div>
          </header>

          <main className="mx-auto max-w-[1600px] p-5 sm:p-8">
            <div className="mb-7 flex flex-wrap items-end justify-between gap-4"><div><p className="mb-2 text-[11px] font-bold tracking-[0.2em] text-[#0891b2]">MARIOT GROUP</p><h1 className="font-display text-3xl text-slate-900 sm:text-4xl">{currentSection.label}</h1><p className="mt-1 text-sm text-slate-500">{currentSection.description}</p></div><div className="rounded-xl border border-slate-200 bg-white px-4 py-2 text-xs font-medium text-slate-500 shadow-sm">Secure admin workspace</div></div>
            <TabsContent value="dashboard" className="mt-0"><DashboardOverview key={`dashboard-${refreshKey}`} onNavigate={setActiveTab} /></TabsContent>
            <TabsContent value="projects" className="mt-0"><ProjectsPanel key={`projects-${refreshKey}`} /></TabsContent>
            <TabsContent value="quotations" className="mt-0"><QuotationsPanel key={`quotations-${refreshKey}`} /></TabsContent>
            <TabsContent value="blog" className="mt-0"><BlogPanel key={`blog-${refreshKey}`} /></TabsContent>
            <TabsContent value="homepage" className="mt-0"><HomepageImagesPanel key={`homepage-${refreshKey}`} /></TabsContent>
            <TabsContent value="media" className="mt-0"><MediaPanel key={`media-${refreshKey}`} /></TabsContent>
            <TabsContent value="brands" className="mt-0"><BrandsPanel key={`brands-${refreshKey}`} /></TabsContent>
            <TabsContent value="branches" className="mt-0"><BranchesPanel key={`branches-${refreshKey}`} /></TabsContent>
            <TabsContent value="settings" className="mt-0"><SettingsPanel key={`settings-${refreshKey}`} /></TabsContent>
          </main>
        </div>
      </Tabs>
    </div>
  );
}

function Tab({ val, testid, icon: Icon, children }) {
  return (
    <TabsTrigger value={val} data-testid={testid} className="w-full justify-start gap-3 rounded-xl px-4 py-3 text-left text-sm font-semibold text-slate-400 transition-colors hover:bg-white/5 hover:text-white data-[state=active]:bg-[#1d3155] data-[state=active]:text-white data-[state=active]:shadow-sm">
      {Icon && <Icon className="h-4 w-4" />} {children}
    </TabsTrigger>
  );
}

function DashboardOverview({ onNavigate }) {
  const [counts, setCounts] = useState({ projects: 0, blog: 0, brands: 0, branches: 0 });

  useEffect(() => {
    Promise.all([api.get("/projects"), api.get("/blog"), api.get("/brands"), api.get("/branches")])
      .then(([projects, blog, brands, branches]) => setCounts({ projects: projects.data?.length || 0, blog: blog.data?.length || 0, brands: brands.data?.length || 0, branches: branches.data?.length || 0 }))
      .catch(() => {});
  }, []);

  const cards = [
    { label: "Projects", value: counts.projects, caption: "Portfolio entries", icon: FolderKanban, tone: "border-blue-200 bg-blue-50/70 text-blue-600", tab: "projects" },
    { label: "Blog posts", value: counts.blog, caption: "Published articles", icon: Newspaper, tone: "border-violet-200 bg-violet-50/70 text-violet-600", tab: "blog" },
    { label: "Brands", value: counts.brands, caption: "Partner profiles", icon: Tags, tone: "border-amber-200 bg-amber-50/70 text-amber-600", tab: "brands" },
    { label: "Branches", value: counts.branches, caption: "Service locations", icon: MapPinIcon, tone: "border-emerald-200 bg-emerald-50/70 text-emerald-600", tab: "branches" },
  ];

  return <div className="space-y-6" data-testid="dashboard-overview"><div className="rounded-2xl border border-slate-200 border-l-4 border-l-[#2563eb] bg-white p-6 shadow-sm sm:flex sm:items-center sm:justify-between"><div><h2 className="font-display text-2xl text-slate-900">Welcome back</h2><p className="mt-1 text-sm text-slate-500">Manage your website content, images, projects, and blog posts from one place.</p></div><Button onClick={() => onNavigate("homepage")} className="mt-4 bg-[#2563eb] text-white hover:bg-blue-700 sm:mt-0"><Images className="me-2 h-4 w-4" />Manage page images</Button></div><div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-4">{cards.map((card) => { const Icon = card.icon; return <button key={card.label} onClick={() => onNavigate(card.tab)} className={`rounded-2xl border p-5 text-left transition-transform hover:-translate-y-0.5 hover:shadow-md ${card.tone}`}><div className="mb-5 flex items-start justify-between"><p className="text-xs font-bold uppercase tracking-wider text-slate-500">{card.label}</p><span className="rounded-xl bg-white/80 p-2"><Icon className="h-5 w-5" /></span></div><p className="font-display text-4xl text-slate-900">{card.value}</p><p className="mt-1 text-sm text-slate-500">{card.caption}</p></button>; })}</div><div className="grid gap-6 lg:grid-cols-2"><div className="rounded-2xl border border-slate-200 bg-white p-6 shadow-sm"><h3 className="font-display text-xl text-slate-900">Quick actions</h3><p className="mt-1 text-sm text-slate-500">Add new content without leaving the dashboard.</p><div className="mt-5 grid gap-3 sm:grid-cols-2"><Button onClick={() => onNavigate("projects")} variant="outline" className="justify-start"><FolderKanban className="me-2 h-4 w-4 text-[#2563eb]" />Manage projects</Button><Button onClick={() => onNavigate("blog")} variant="outline" className="justify-start"><Newspaper className="me-2 h-4 w-4 text-[#2563eb]" />Write blog content</Button></div></div><div className="rounded-2xl border border-cyan-100 bg-cyan-50/60 p-6"><h3 className="font-display text-xl text-slate-900">Image management</h3><p className="mt-1 text-sm text-slate-600">Replace the existing image in any named website section while keeping its layout unchanged.</p><Button onClick={() => onNavigate("homepage")} className="mt-5 bg-[#0891b2] text-white hover:bg-cyan-700"><ImageIcon className="me-2 h-4 w-4" />Open image manager</Button></div></div></div>;
}

function ImageUploadField({ label, value, onChange, testid }) {
  const [uploading, setUploading] = useState(false);

  const upload = async (file) => {
    if (!file) return;
    if (!file.type.startsWith("image/")) {
      toast.error("Please choose an image file");
      return;
    }
    setUploading(true);
    try {
      const form = new FormData();
      form.append("file", file);
      const { data } = await api.post("/upload", form, { headers: { "Content-Type": "multipart/form-data" } });
      onChange(mediaUrl(data.path));
      toast.success("Image uploaded");
    } catch (error) {
      toast.error(error.response?.data?.message || "Image upload failed");
    } finally {
      setUploading(false);
    }
  };

  return (
    <AF label={label}>
      <div className="flex gap-2">
        <Input value={value || ""} onChange={(event) => onChange(event.target.value)} placeholder="Paste an image URL or upload a file" data-testid={testid} />
        <label className="shrink-0 inline-flex items-center justify-center w-10 h-10 rounded border border-slate-300 cursor-pointer hover:border-[#0891b2]" title="Upload image">
          <Upload className="w-4 h-4 text-slate-600" />
          <input className="hidden" type="file" accept="image/jpeg,image/png,image/webp,image/gif" disabled={uploading} onChange={(event) => upload(event.target.files?.[0])} />
        </label>
      </div>
      {uploading && <p className="text-xs text-slate-500">Uploading image…</p>}
      {value && <img src={value} alt="Preview" className="mt-2 h-24 w-36 object-cover rounded border border-slate-200" />}
    </AF>
  );
}

function GalleryUploader({ value, onChange }) {
  const [uploading, setUploading] = useState(false);
  const gallery = value || [];

  const upload = async (files) => {
    const images = Array.from(files || []).filter((file) => file.type.startsWith("image/"));
    if (!images.length) return;
    setUploading(true);
    try {
      const uploaded = await Promise.all(images.map(async (file) => {
        const form = new FormData();
        form.append("file", file);
        const { data } = await api.post("/upload", form, { headers: { "Content-Type": "multipart/form-data" } });
        return mediaUrl(data.path);
      }));
      onChange([...gallery, ...uploaded]);
      toast.success(`${uploaded.length} image${uploaded.length === 1 ? "" : "s"} uploaded`);
    } catch (error) {
      toast.error(error.response?.data?.message || "Gallery upload failed");
    } finally {
      setUploading(false);
    }
  };

  return (
    <div className="sm:col-span-2 space-y-2">
      <Label className="text-[11px] uppercase text-slate-500 tracking-widest">Project gallery</Label>
      <div className="flex flex-wrap gap-2">
        {gallery.map((image, index) => (
          <div key={`${image}-${index}`} className="relative">
            <img src={image} alt="Gallery preview" className="w-24 h-16 object-cover rounded border border-slate-200" />
            <button type="button" onClick={() => onChange(gallery.filter((_, itemIndex) => itemIndex !== index))} className="absolute -top-2 -right-2 w-5 h-5 rounded-full bg-red-600 text-white flex items-center justify-center" aria-label="Remove image"><X className="w-3 h-3" /></button>
          </div>
        ))}
        <label className="w-24 h-16 border border-dashed border-slate-300 rounded flex flex-col items-center justify-center text-xs text-slate-500 cursor-pointer hover:border-[#0891b2]">
          <Upload className="w-4 h-4 mb-1" /> {uploading ? "Uploading…" : "Add images"}
          <input className="hidden" type="file" multiple accept="image/jpeg,image/png,image/webp,image/gif" disabled={uploading} onChange={(event) => upload(event.target.files)} />
        </label>
      </div>
    </div>
  );
}

function MediaPanel() {
  const [image, setImage] = useState("");
  return (
    <div className="max-w-xl bg-white rounded-lg border border-slate-200 p-6" data-testid="media-panel">
      <div className="flex gap-3 mb-5">
        <div className="w-10 h-10 rounded bg-cyan-50 flex items-center justify-center"><ImageIcon className="w-5 h-5 text-[#0891b2]" /></div>
        <div><h2 className="font-display text-xl text-slate-900">Upload media</h2><p className="text-sm text-slate-500">Upload an image, then use its link in any project, news post, brand, or branch.</p></div>
      </div>
      <ImageUploadField label="Image" value={image} onChange={setImage} testid="media-image" />
      {image && <p className="mt-4 p-3 bg-slate-50 rounded text-xs text-slate-600 break-all">{image}</p>}
    </div>
  );
}

const HOMEPAGE_IMAGE_FIELDS = [
  { key: "home_hero", label: "Hero banner", fallback: "https://images.unsplash.com/photo-1749478072094-d21cb929490c?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NTYxODl8MHwxfHNlYXJjaHwxfHxpbmR1c3RyaWFsJTIwa2l0Y2hlbiUyMHN0YWlubGVzcyUyMHN0ZWVsJTdDfGVufDB8fHx8MTc4MjMxOTA3OXww&ixlib=rb-4.1.0&q=85" },
  { key: "home_factory", label: "Stainless factory section", fallback: "https://images.unsplash.com/photo-1556909114-f6e7ad7d3136?auto=format&fit=crop&w=1000&q=80" },
  { key: "home_shop", label: "Mariot Store section", fallback: "https://images.pexels.com/photos/2977514/pexels-photo-2977514.jpeg" },
  { key: "home_package_1", label: "Package card — Restaurant", fallback: "https://images.unsplash.com/photo-1517248135467-4c7edcad34c4?auto=format&fit=crop&w=800&q=80" },
  { key: "home_package_2", label: "Package card — Café", fallback: "https://images.unsplash.com/photo-1447933601403-0c6688de566e?auto=format&fit=crop&w=800&q=80" },
  { key: "home_package_3", label: "Package card — Bakery", fallback: "https://images.unsplash.com/photo-1509440159596-0249088772ff?auto=format&fit=crop&w=800&q=80" },
  { key: "home_package_4", label: "Package card — Budget", fallback: "https://images.unsplash.com/photo-1556909114-f6e7ad7d3136?auto=format&fit=crop&w=800&q=80" },
  { key: "home_package_5", label: "Package card — Custom", fallback: "https://images.unsplash.com/photo-1556910103-1c02745aae4d?auto=format&fit=crop&w=800&q=80" },
];

const PAGE_IMAGE_GROUPS = [
  { page: "Home", fields: HOMEPAGE_IMAGE_FIELDS },
  { page: "About", fields: [
    { key: "about_main_image", label: "About us section image", fallback: "https://images.pexels.com/photos/2977514/pexels-photo-2977514.jpeg" },
  ] },
  { page: "Business Hub", fields: [
    { key: "business_hub_card_1", label: "Card 1 image", fallback: "https://images.unsplash.com/photo-1552664730-d307ca884978?auto=format&fit=crop&w=1000&q=80" },
    { key: "business_hub_card_2", label: "Card 2 image", fallback: "https://images.unsplash.com/photo-1556909114-f6e7ad7d3136?auto=format&fit=crop&w=1000&q=80" },
    { key: "business_hub_card_3", label: "Card 3 image", fallback: "https://images.unsplash.com/photo-1445019980597-93fa8acb246c?auto=format&fit=crop&w=1000&q=80" },
    { key: "business_hub_card_4", label: "Card 4 image", fallback: "https://images.unsplash.com/photo-1504674900247-0877df9cc836?auto=format&fit=crop&w=1000&q=80" },
  ] },
  { page: "Design", fields: [
    { key: "design_hero", label: "Hero image", fallback: "https://images.pexels.com/photos/2977514/pexels-photo-2977514.jpeg" },
    { key: "design_blueprint", label: "Stages - Blueprint image", fallback: "https://images.pexels.com/photos/37320179/pexels-photo-37320179.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940" },
    { key: "design_3d_render", label: "Stages - 3D render image", fallback: "https://images.unsplash.com/photo-1749478072094-d21cb929490c?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NTYxODl8MHwxfHNlYXJjaHwxfHxpbmR1c3RyaWFsJTIwa2l0Y2hlbiUyMHN0YWlubGVzcyUyMHN0ZWVsJTdDfGVufDB8fHx8MTc4MjMxOTA3OXww&ixlib=rb-4.1.0&q=85" },
    { key: "design_execution", label: "Stages - Execution image", fallback: "https://images.pexels.com/photos/358549/pexels-photo-358549.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940" },
    { key: "design_viewer_preview", label: "3D viewer preview image", fallback: "https://images.pexels.com/photos/2696064/pexels-photo-2696064.jpeg?auto=compress&cs=tinysrgb&w=1600" },
  ] },
  { page: "Fabrication", fields: [
    { key: "fabrication_hero", label: "Hero image", fallback: "https://images.unsplash.com/photo-1556909114-f6e7ad7d3136?auto=format&fit=crop&w=1000&q=80" },
    { key: "fabrication_gallery_1", label: "Gallery - Work tables", fallback: "https://images.unsplash.com/photo-1556909114-f6e7ad7d3136?auto=format&fit=crop&w=800&q=80" },
    { key: "fabrication_gallery_2", label: "Gallery - Sinks", fallback: "https://images.unsplash.com/photo-1556909172-54557c7e4fb7?auto=format&fit=crop&w=800&q=80" },
    { key: "fabrication_gallery_3", label: "Gallery - Shelves", fallback: "https://images.unsplash.com/photo-1621263764928-df1444c5e859?auto=format&fit=crop&w=800&q=80" },
    { key: "fabrication_gallery_4", label: "Gallery - Hoods", fallback: "https://images.unsplash.com/photo-1585237017125-24baf8d7406f?auto=format&fit=crop&w=800&q=80" },
    { key: "fabrication_gallery_5", label: "Gallery - Trolleys", fallback: "https://images.unsplash.com/photo-1556909195-27b0b96e2e7d?auto=format&fit=crop&w=800&q=80" },
    { key: "fabrication_gallery_6", label: "Gallery - Counters", fallback: "https://images.unsplash.com/photo-1591189863430-ab87e120f312?auto=format&fit=crop&w=800&q=80" },
    { key: "fabrication_gallery_7", label: "Gallery - Cabinets", fallback: "https://images.unsplash.com/photo-1556910096-6f5e72db6803?auto=format&fit=crop&w=800&q=80" },
    { key: "fabrication_gallery_8", label: "Gallery - Custom build", fallback: "https://images.unsplash.com/photo-1600607687920-4e2a09cf159d?auto=format&fit=crop&w=800&q=80" },
  ] },
  { page: "How We Work", fields: [
    { key: "how_we_work_step_1", label: "Step 1 - Consultation", fallback: "https://images.unsplash.com/photo-1552664730-d307ca884978?auto=format&fit=crop&w=500&q=75" },
    { key: "how_we_work_step_2", label: "Step 2 - Site visit", fallback: "https://images.unsplash.com/photo-1497366216548-37526070297c?auto=format&fit=crop&w=500&q=75" },
    { key: "how_we_work_step_3", label: "Step 3 - Kitchen planning", fallback: "https://images.unsplash.com/photo-1454165804606-c3d57bc86b40?auto=format&fit=crop&w=500&q=75" },
    { key: "how_we_work_step_4", label: "Step 4 - Equipment selection", fallback: "https://images.unsplash.com/photo-1556910096-6f5e72db6803?auto=format&fit=crop&w=500&q=75" },
    { key: "how_we_work_step_5", label: "Step 5 - BOQ preparation", fallback: "https://images.unsplash.com/photo-1450101499163-c8848c66ca85?auto=format&fit=crop&w=500&q=75" },
    { key: "how_we_work_step_6", label: "Step 6 - Stainless steel fabrication", fallback: "https://images.unsplash.com/photo-1556909114-f6e7ad7d3136?auto=format&fit=crop&w=500&q=75" },
    { key: "how_we_work_step_7", label: "Step 7 - Supply and delivery", fallback: "https://images.unsplash.com/photo-1601584115197-04ecc0da31d7?auto=format&fit=crop&w=500&q=75" },
    { key: "how_we_work_step_8", label: "Step 8 - Installation", fallback: "https://images.unsplash.com/photo-1581093458791-9d15482442f6?auto=format&fit=crop&w=500&q=75" },
    { key: "how_we_work_step_9", label: "Step 9 - Testing and commissioning", fallback: "https://images.unsplash.com/photo-1581094794329-c8112a89af12?auto=format&fit=crop&w=500&q=75" },
    { key: "how_we_work_step_10", label: "Step 10 - Training", fallback: "https://images.unsplash.com/photo-1521737604893-d14cc237f11d?auto=format&fit=crop&w=500&q=75" },
    { key: "how_we_work_step_11", label: "Step 11 - Maintenance and support", fallback: "https://images.unsplash.com/photo-1600880292203-757bb62b4baf?auto=format&fit=crop&w=500&q=75" },
  ] },
  { page: "Sectors", fields: [
    { key: "sector_restaurant", label: "Restaurant sector image", fallback: "https://images.unsplash.com/photo-1517248135467-4c7edcad34c4?auto=format&fit=crop&w=600&q=75" },
    { key: "sector_cafe", label: "Cafe sector image", fallback: "https://images.unsplash.com/photo-1554118811-1e0d58224f24?auto=format&fit=crop&w=600&q=75" },
    { key: "sector_hotel", label: "Hotel sector image", fallback: "https://images.unsplash.com/photo-1566073771259-6a8506099945?auto=format&fit=crop&w=600&q=75" },
    { key: "sector_resort", label: "Resort sector image", fallback: "https://images.unsplash.com/photo-1571003123894-1f0594d2b5d9?auto=format&fit=crop&w=600&q=75" },
    { key: "sector_villa_palace", label: "Villa and palace sector image", fallback: "https://images.unsplash.com/photo-1600585154340-be6161a56a0c?auto=format&fit=crop&w=600&q=75" },
    { key: "sector_hospital", label: "Hospital sector image", fallback: "https://images.unsplash.com/photo-1519494026892-80bbd2d6fd0d?auto=format&fit=crop&w=600&q=75" },
    { key: "sector_bakery", label: "Bakery sector image", fallback: "https://images.unsplash.com/photo-1509440159596-0249088772ff?auto=format&fit=crop&w=600&q=75" },
    { key: "sector_laundry", label: "Laundry sector image", fallback: "https://images.unsplash.com/photo-1626806787461-102c1bfaaea1?auto=format&fit=crop&w=600&q=75" },
    { key: "sector_catering", label: "Catering sector image", fallback: "https://images.unsplash.com/photo-1555244162-803834f70033?auto=format&fit=crop&w=600&q=75" },
    { key: "sector_supermarket", label: "Supermarket sector image", fallback: "https://images.unsplash.com/photo-1542838132-92c53300491e?auto=format&fit=crop&w=600&q=75" },
    { key: "sector_cloud_kitchen", label: "Cloud kitchen sector image", fallback: "https://images.unsplash.com/photo-1556909114-f6e7ad7d3136?auto=format&fit=crop&w=600&q=75" },
    { key: "sector_central_kitchen", label: "Central kitchen sector image", fallback: "https://images.unsplash.com/photo-1600607687920-4e2a09cf159d?auto=format&fit=crop&w=600&q=75" },
  ] },
  { page: "logo", fields: [
    { key: "site_logo_en", label: "Header logo - English", fallback: "/assets/mariotlogoen.png" },
    { key: "site_logo_ar", label: "Header logo - Arabic", fallback: "/assets/mariotlogoar.png" },
  ] },
];

function HomepageImagesPanel() {
  const [images, setImages] = useState({});
  const [saving, setSaving] = useState(false);
  const [selectedPage, setSelectedPage] = useState(PAGE_IMAGE_GROUPS[0].page);
  const currentGroup = PAGE_IMAGE_GROUPS.find((group) => group.page === selectedPage) || PAGE_IMAGE_GROUPS[0];

  useEffect(() => { api.get("/settings").then((response) => setImages(response.data || {})).catch(() => setImages({})); }, []);

  const save = async () => {
    setSaving(true);
    try {
      await Promise.all(currentGroup.fields.map((field) => api.put(`/settings/${field.key}`, { value: images[field.key] || field.fallback })));
      toast.success(`${currentGroup.page} page images updated`);
    } catch (error) {
      toast.error(error.response?.data?.message || `Could not save ${currentGroup.page} page images`);
    } finally { setSaving(false); }
  };

  return (
    <div className="max-w-6xl" data-testid="page-images-panel">
      <div className="mb-5"><h2 className="font-display text-2xl text-slate-900">Page images</h2><p className="text-sm text-slate-500 mt-1">Select a page, then update only the named image sections for that page.</p></div>
      <div className="grid gap-5 lg:grid-cols-[280px_1fr]">
        <aside className="h-fit rounded-xl border border-slate-200 bg-white p-4">
          <Label className="text-[11px] uppercase tracking-widest text-slate-500">Choose page</Label>
          <Select value={selectedPage} onValueChange={setSelectedPage}>
            <SelectTrigger className="mt-2 h-11" data-testid="page-image-selector"><SelectValue /></SelectTrigger>
            <SelectContent>{PAGE_IMAGE_GROUPS.map((group) => <SelectItem key={group.page} value={group.page}>{group.page} page</SelectItem>)}</SelectContent>
          </Select>
          <div className="mt-4 space-y-1 border-t border-slate-100 pt-4">
            {PAGE_IMAGE_GROUPS.map((group) => <button key={group.page} type="button" onClick={() => setSelectedPage(group.page)} className={`w-full rounded-lg px-3 py-2 text-left text-sm font-medium transition-colors ${selectedPage === group.page ? "bg-cyan-50 text-[#0891b2]" : "text-slate-600 hover:bg-slate-50"}`}>{group.page}</button>)}
          </div>
        </aside>
        <section className="rounded-xl border border-slate-200 bg-slate-50/60 p-4 sm:p-5">
          <div className="mb-4"><h3 className="font-display text-xl text-slate-900">{currentGroup.page} page</h3><p className="text-xs text-slate-500">Image controls for this page only.</p></div>
          <div className="grid gap-4 md:grid-cols-2">
            {currentGroup.fields.map((field) => (
              <div key={field.key} className="rounded-lg border border-slate-200 bg-white p-4">
                <ImageUploadField label={field.label} value={images[field.key] || field.fallback} onChange={(value) => setImages((current) => ({ ...current, [field.key]: value }))} testid={`page-image-${field.key}`} />
              </div>
            ))}
          </div>
        </section>
      </div>
      <div className="sticky bottom-4 mt-6 flex justify-end">
        <Button onClick={save} disabled={saving} data-testid="save-page-images" className="bg-[#0891b2] hover:bg-[#0e7490] text-white shadow-lg">{saving ? "Saving..." : `Save ${currentGroup.page} images`}<Save className="w-4 h-4 ms-2" /></Button>
      </div>
    </div>
  );
}

// ============ QUOTATIONS ============
const SOURCE_TYPES = [
  { key: "all", label: "All", color: "bg-slate-100 text-slate-700" },
  { key: "quotation", label: "General Quote", color: "bg-blue-100 text-blue-700" },
  { key: "business_account", label: "Business Account", color: "bg-indigo-100 text-indigo-700" },
  { key: "consultation", label: "Consultation", color: "bg-green-100 text-green-700" },
  { key: "restaurant_opening", label: "Restaurant Opening", color: "bg-amber-100 text-amber-700" },
  { key: "maintenance", label: "Maintenance", color: "bg-teal-100 text-teal-700" },
  { key: "urgent", label: "Urgent", color: "bg-red-100 text-red-700" },
];

function QuotationsPanel() {
  const { t } = useLang();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expanded, setExpanded] = useState(null);
  const [typeFilter, setTypeFilter] = useState("all");

  const load = () => {
    setLoading(true);
    api.get("/quotations")
      .then((r) => setItems(r.data || []))
      .catch((error) => {
        if (error.response?.status === 401) {
          localStorage.removeItem("mk_token");
          localStorage.removeItem("mk_admin_email");
          toast.error("Your admin session has expired. Please sign in again.");
          window.setTimeout(() => window.location.assign("/admin/login"), 300);
          return;
        }
        setItems([]);
        toast.error("Could not load quotations. Please try again.");
      })
      .finally(() => setLoading(false));
  };
  useEffect(() => { load(); }, []);

  const update = async (id, patch) => {
    await api.patch(`/quotations/${id}`, patch);
    toast.success("Updated");
    load();
  };

  const del = async (id) => {
    if (!window.confirm("Delete this request?")) return;
    await api.delete(`/quotations/${id}`);
    load();
  };

  // Filter items by source type
  const filteredItems = typeFilter === "all"
    ? items
    : items.filter((q) => (q.source || "quotation") === typeFilter);

  // Count per type
  const countBy = (key) => key === "all" ? items.length : items.filter((q) => (q.source || "quotation") === key).length;

  if (loading) return <p className="text-slate-500 p-8 text-center">Loading...</p>;

  const statusColor = (s) => ({
    new: "text-red-600 bg-red-50", contacted: "text-blue-600 bg-cyan-50",
    quotation_sent: "text-purple-600 bg-purple-50", waiting_customer: "text-orange-600 bg-orange-50",
    approved: "text-green-600 bg-green-50", rejected: "text-slate-500 bg-slate-100",
    closed: "text-slate-400 bg-slate-50",
  })[s] || "";

  const sourceColor = SOURCE_TYPES.find(s => s.key === (typeFilter === "all" ? "all" : typeFilter))?.color || "bg-slate-100";

  return (
    <div className="space-y-3" data-testid="quotations-list">
      {/* Filter tabs */}
      <div className="flex flex-wrap gap-2 mb-4 bg-white p-3 rounded-lg border border-slate-200" data-testid="qtype-filter">
        {SOURCE_TYPES.map((s) => (
          <button
            key={s.key}
            type="button"
            onClick={() => setTypeFilter(s.key)}
            data-testid={`filter-type-${s.key}`}
            className={`text-xs font-medium px-3 py-1.5 rounded-full transition-all ${
              typeFilter === s.key
                ? `${s.color} ring-2 ring-offset-1 ring-slate-300`
                : "bg-slate-50 text-slate-500 hover:bg-slate-100"
            }`}
          >
            {s.label} <span className="ms-1 opacity-70">({countBy(s.key)})</span>
          </button>
        ))}
      </div>

      {filteredItems.length === 0 ? (
        <p className="text-slate-500 py-16 text-center bg-white rounded-lg" data-testid="quotations-empty">
          No requests in this category yet.
        </p>
      ) : filteredItems.map((q, i) => {
        const src = SOURCE_TYPES.find(x => x.key === (q.source || "quotation")) || SOURCE_TYPES[1];
        return (
        <div key={q.id} className="bg-white rounded-lg border border-slate-200" data-testid={`quote-row-${i}`}>
          <div className="p-5 grid grid-cols-1 md:grid-cols-12 gap-4 items-center">
            <div className="md:col-span-4">
              <div className="flex items-center gap-2 mb-1">
                <span className={`text-[10px] uppercase tracking-widest px-2 py-0.5 rounded ${src.color} font-semibold`}>
                  {src.label}
                </span>
                <span className="text-[10px] font-bold tracking-wider text-[#0891b2]">{q.customer_id || `Q-${q.id}`}</span>
              </div>
              <div className="font-semibold text-slate-900">{q.name}</div>
              <div className="text-xs text-slate-500 flex flex-col gap-0.5 mt-1">
                <a href={`tel:${q.phone}`} className="flex items-center gap-1 hover:text-[#0891b2]" dir="ltr"><Phone className="w-3 h-3" />{q.phone}</a>
                {q.email && <a href={`mailto:${q.email}`} className="flex items-center gap-1 hover:text-[#0891b2]" dir="ltr"><Mail className="w-3 h-3" />{q.email}</a>}
              </div>
            </div>
            <div className="md:col-span-2">
              <div className="text-[11px] uppercase text-slate-500 tracking-wider">Project</div>
              <div className="text-sm text-slate-800">{t.categories[q.project_type]}</div>
              <div className="text-xs text-slate-500">{t.emirates[q.emirate]}</div>
            </div>
            <div className="md:col-span-2">
              <div className="text-[11px] uppercase text-slate-500 tracking-wider">Submitted</div>
              <div className="text-sm text-slate-800">{q.created_at ? new Date(q.created_at).toLocaleDateString() : "—"}</div>
              <div className="text-xs text-slate-500">{q.created_at ? new Date(q.created_at).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" }) : ""}</div>
            </div>
            <div className="md:col-span-2">
              <Select value={q.status} onValueChange={(v) => update(q.id, { status: v })}>
                <SelectTrigger data-testid={`status-${i}`} className={`h-9 text-xs font-medium ${statusColor(q.status)}`}>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {STATUSES.map((s) => <SelectItem key={s} value={s}>{t.statuses[s]}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="md:col-span-2">
              <Input placeholder="Assigned staff" defaultValue={q.assigned_staff}
                     onBlur={(e) => e.target.value !== q.assigned_staff && update(q.id, { assigned_staff: e.target.value })}
                     className="h-9 text-xs" data-testid={`staff-${i}`} />
            </div>
            <div className="md:col-span-1 flex justify-end gap-1">
              <button onClick={() => setExpanded(expanded === q.id ? null : q.id)}
                      data-testid={`expand-${i}`} className="inline-flex items-center gap-1 text-xs font-semibold text-[#0891b2] hover:underline">
                {expanded === q.id ? "Hide" : "View"} <ChevronDown className={`w-3.5 h-3.5 transition-transform ${expanded === q.id ? "rotate-180" : ""}`} />
              </button>
              <button onClick={() => del(q.id)} data-testid={`delete-quote-${i}`} className="text-slate-400 hover:text-red-600 p-1">
                <Trash2 className="w-4 h-4" />
              </button>
            </div>
          </div>
          {expanded === q.id && (
            <div className="border-t border-slate-100 p-5 bg-slate-50 space-y-3 text-sm" data-testid={`details-${i}`}>
              {q.company_name && <div><span className="text-slate-500">Company:</span> {q.company_name} {q.company_website && <a href={q.company_website} target="_blank" rel="noreferrer" className="text-[#0891b2] ms-2"><ExternalLink className="w-3 h-3 inline" /></a>}</div>}
              <div><span className="text-slate-500">Preferred contact:</span> {q.preferred_contact}</div>
              {q.project_reference && <div><span className="text-slate-500">Reference:</span> {q.project_reference}</div>}
              <div><span className="text-slate-500">Message:</span> <div className="mt-1 whitespace-pre-line text-slate-700">{q.message}</div></div>
              {q.boq_file_path && (
                <div>
                  <span className="text-slate-500">Attachment:</span>{" "}
                  <a href={mediaUrl(q.boq_file_path)} target="_blank" rel="noreferrer"
                     className="text-[#0891b2] hover:underline inline-flex items-center gap-1" data-testid={`file-link-${i}`}>
                    <FileText className="w-3.5 h-3.5" /> {q.boq_file_name || "download"}
                  </a>
                </div>
              )}
              <div>
                <div className="text-slate-500 mb-1">Notes:</div>
                <Textarea defaultValue={q.notes} rows={2} className="text-sm"
                          onBlur={(e) => e.target.value !== q.notes && update(q.id, { notes: e.target.value })}
                          data-testid={`notes-${i}`} />
              </div>
              <div className="text-xs text-slate-400">Submitted: {new Date(q.created_at).toLocaleString()}</div>
            </div>
          )}
        </div>
        );
      })}
    </div>
  );
}

// ============ PROJECTS ============
const emptyProject = {
  slug: "", title_en: "", title_ar: "", client_name: "", category: "restaurant", emirate: "dubai",
  location_en: "", location_ar: "", completion_date: "", description_en: "", description_ar: "",
  scope_en: "", scope_ar: "", equipment_en: "", equipment_ar: "", brands_used: [], image: "",
  gallery: [], featured: false,
};

function ProjectsPanel() {
  const [items, setItems] = useState([]);
  const [editing, setEditing] = useState(null);
  const [open, setOpen] = useState(false);

  const load = () => api.get("/projects").then((r) => setItems(r.data));
  useEffect(() => { load(); }, []);

  const save = async (data) => {
    const payload = { ...data, brands_used: typeof data.brands_used === "string" ? data.brands_used.split(",").map((s) => s.trim()).filter(Boolean) : data.brands_used };
    try {
      if (data.id) await api.put(`/projects/${data.id}`, payload);
      else await api.post("/projects", payload);
      toast.success("Project published");
      setOpen(false);
      load();
    } catch (error) { toast.error(error.response?.data?.message || "Could not save project"); }
  };

  const del = async (id) => {
    if (!window.confirm("Delete?")) return;
    await api.delete(`/projects/${id}`);
    load();
  };

  return (
    <div data-testid="projects-panel">
      <div className="flex justify-end mb-4">
        <Button onClick={() => { setEditing(emptyProject); setOpen(true); }} data-testid="add-project-btn"
                className="bg-[#ef4444] hover:bg-[#dc2626] text-white h-10">
          <Plus className="w-4 h-4 me-2" /> Add Project
        </Button>
      </div>
      <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
        {items.map((p, i) => (
          <div key={p.id} className="bg-white border border-slate-200 rounded-lg p-4" data-testid={`admin-project-${i}`}>
            <img src={p.image} alt="" className="aspect-video object-cover w-full rounded mb-3" />
            <h3 className="font-semibold text-slate-900 mb-1 line-clamp-1">{p.title_en}</h3>
            <div className="text-xs text-slate-500 mb-3">{p.category} · {p.emirate} {p.featured && "· ⭐"}</div>
            <div className="flex gap-2">
              <button onClick={() => { setEditing({ ...p, brands_used: p.brands_used?.join(", ") || "" }); setOpen(true); }} data-testid={`edit-project-${i}`} className="text-xs text-[#0891b2] hover:underline flex items-center gap-1"><Pencil className="w-3 h-3" /> Edit</button>
              <button onClick={() => del(p.id)} data-testid={`delete-project-${i}`} className="text-xs text-slate-500 hover:text-red-600 ms-auto flex items-center gap-1"><Trash2 className="w-3 h-3" /> Delete</button>
            </div>
          </div>
        ))}
      </div>
      <ProjectDialog open={open} onOpenChange={setOpen} editing={editing} onSave={save} />
    </div>
  );
}

function ProjectDialog({ open, onOpenChange, editing, onSave }) {
  const [f, setF] = useState(emptyProject);
  useEffect(() => { if (editing) setF(editing); }, [editing]);
  const upd = (k, v) => setF((p) => ({ ...p, [k]: v }));

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>{f.id ? "Edit" : "New"} Project</DialogTitle>
        </DialogHeader>
        <div className="grid sm:grid-cols-2 gap-3">
          <AF label="Title (EN)"><Input value={f.title_en} onChange={(e) => upd("title_en", e.target.value)} data-testid="p-title-en" /></AF>
          <AF label="Title (AR)"><Input dir="rtl" value={f.title_ar} onChange={(e) => upd("title_ar", e.target.value)} data-testid="p-title-ar" /></AF>
          <div className="sm:col-span-2"><AF label="Page URL (optional)"><Input value={f.slug} onChange={(e) => upd("slug", e.target.value)} placeholder="Created automatically from the English title" /></AF></div>
          <AF label="Client"><Input value={f.client_name} onChange={(e) => upd("client_name", e.target.value)} /></AF>
          <AF label="Completion date"><Input value={f.completion_date} onChange={(e) => upd("completion_date", e.target.value)} /></AF>
          <AF label="Category">
            <Select value={f.category} onValueChange={(v) => upd("category", v)}>
              <SelectTrigger><SelectValue /></SelectTrigger>
              <SelectContent>{CATEGORIES.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
            </Select>
          </AF>
          <AF label="Emirate">
            <Select value={f.emirate} onValueChange={(v) => upd("emirate", v)}>
              <SelectTrigger><SelectValue /></SelectTrigger>
              <SelectContent>{EMIRATES.map((e) => <SelectItem key={e} value={e}>{e}</SelectItem>)}</SelectContent>
            </Select>
          </AF>
          <AF label="Location (EN)"><Input value={f.location_en} onChange={(e) => upd("location_en", e.target.value)} /></AF>
          <AF label="Location (AR)"><Input dir="rtl" value={f.location_ar} onChange={(e) => upd("location_ar", e.target.value)} /></AF>
          <div className="sm:col-span-2"><AF label="Description (EN)"><Textarea rows={3} value={f.description_en} onChange={(e) => upd("description_en", e.target.value)} /></AF></div>
          <div className="sm:col-span-2"><AF label="Description (AR)"><Textarea rows={3} dir="rtl" value={f.description_ar} onChange={(e) => upd("description_ar", e.target.value)} /></AF></div>
          <AF label="Scope (EN)"><Textarea rows={2} value={f.scope_en} onChange={(e) => upd("scope_en", e.target.value)} /></AF>
          <AF label="Scope (AR)"><Textarea rows={2} dir="rtl" value={f.scope_ar} onChange={(e) => upd("scope_ar", e.target.value)} /></AF>
          <AF label="Equipment (EN)"><Textarea rows={2} value={f.equipment_en} onChange={(e) => upd("equipment_en", e.target.value)} /></AF>
          <AF label="Equipment (AR)"><Textarea rows={2} dir="rtl" value={f.equipment_ar} onChange={(e) => upd("equipment_ar", e.target.value)} /></AF>
          <div className="sm:col-span-2"><AF label="Brands (comma-separated)"><Input value={f.brands_used} onChange={(e) => upd("brands_used", e.target.value)} placeholder="Rational, Fimar" /></AF></div>
          <div className="sm:col-span-2"><ImageUploadField label="Cover image" value={f.image} onChange={(value) => upd("image", value)} testid="p-image" /></div>
          <GalleryUploader value={f.gallery} onChange={(value) => upd("gallery", value)} />
          <div className="sm:col-span-2">
            <label className="flex items-center gap-2 text-sm">
              <input type="checkbox" checked={f.featured} onChange={(e) => upd("featured", e.target.checked)} /> Featured on home page
            </label>
          </div>
        </div>
        <DialogFooter>
          <Button onClick={() => onSave(f)} data-testid="save-project" className="bg-[#0891b2] hover:bg-[#ef4444] text-white">Save</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ============ BRANDS ============
const emptyBrand = { name: "", country: "", image: "", description_en: "", description_ar: "",
  categories: [], featured_products: [], catalog_url: "" };

function BrandsPanel() {
  const [items, setItems] = useState([]);
  const [editing, setEditing] = useState(null);
  const [open, setOpen] = useState(false);
  const load = () => api.get("/brands").then((r) => setItems(r.data));
  useEffect(() => { load(); }, []);
  const save = async (data) => {
    const payload = { ...data,
      categories: typeof data.categories === "string" ? data.categories.split(",").map((s) => s.trim()).filter(Boolean) : data.categories,
      featured_products: typeof data.featured_products === "string" ? data.featured_products.split(",").map((s) => s.trim()).filter(Boolean) : data.featured_products,
    };
    try {
      if (data.id) await api.put(`/brands/${data.id}`, payload);
      else await api.post("/brands", payload);
      toast.success("Brand saved"); setOpen(false); load();
    } catch (error) { toast.error(error.response?.data?.message || "Could not save brand"); }
  };
  const del = async (id) => { if (!window.confirm("Delete?")) return; await api.delete(`/brands/${id}`); load(); };

  return (
    <div data-testid="brands-panel">
      <div className="flex justify-end mb-4">
        <Button onClick={() => { setEditing(emptyBrand); setOpen(true); }} data-testid="add-brand-btn" className="bg-[#ef4444] text-white h-10"><Plus className="w-4 h-4 me-2" />Add Brand</Button>
      </div>
      <div className="grid md:grid-cols-4 lg:grid-cols-6 gap-1">
        {items.map((b, i) => (
          <div key={b.id} className="bg-white border border-slate-200 rounded-lg p-5" data-testid={`admin-brand-${i}`}>
            <div className="h-24 rounded-lg border border-slate-100 bg-slate-50 flex items-center justify-center p-3 mb-4"><img src={b.image || b.logo} alt={b.name} className="max-h-full max-w-full object-contain" /></div>
            <h3 className="font-display text-xl text-[#0891b2] mb-1">{b.name}</h3>
            <div className="text-xs text-slate-500 mb-3">{b.categories?.length ? b.categories.join(" · ") : b.country || "No categories assigned"}</div>
            {b.description_en && <p className="text-sm text-slate-600 line-clamp-2 mb-3">{b.description_en}</p>}
            <div className="flex gap-2">
              <button onClick={() => { setEditing({ ...b, image: b.image || b.logo || "", categories: b.categories?.join(", ") || "", featured_products: b.featured_products?.join(", ") || "" }); setOpen(true); }} data-testid={`edit-brand-${i}`} className="text-xs text-[#0891b2] hover:underline flex items-center gap-1"><Pencil className="w-3 h-3" />Edit</button>
              <button onClick={() => del(b.id)} data-testid={`delete-brand-${i}`} className="text-xs text-slate-500 hover:text-red-600 ms-auto flex items-center gap-1"><Trash2 className="w-3 h-3" />Delete</button>
            </div>
          </div>
        ))}
      </div>
      <BrandDialog open={open} onOpenChange={setOpen} editing={editing} onSave={save} />
    </div>
  );
}

function BrandDialog({ open, onOpenChange, editing, onSave }) {
  const [f, setF] = useState(emptyBrand);
  useEffect(() => { if (editing) setF(editing); }, [editing]);
  const upd = (k, v) => setF((p) => ({ ...p, [k]: v }));
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader><DialogTitle>{f.id ? "Edit" : "New"} Brand</DialogTitle></DialogHeader>
        <div className="grid sm:grid-cols-2 gap-3">
          <AF label="Name"><Input value={f.name} onChange={(e) => upd("name", e.target.value)} data-testid="b-name" /></AF>
          <AF label="Country"><Input value={f.country} onChange={(e) => upd("country", e.target.value)} /></AF>
          <div className="sm:col-span-2"><AF label="Description (EN)"><Textarea rows={3} value={f.description_en} onChange={(e) => upd("description_en", e.target.value)} /></AF></div>
          <div className="sm:col-span-2"><AF label="Description (AR)"><Textarea rows={3} dir="rtl" value={f.description_ar} onChange={(e) => upd("description_ar", e.target.value)} /></AF></div>
          <AF label="Categories (comma-separated)"><Input value={f.categories} onChange={(e) => upd("categories", e.target.value)} /></AF>
          <AF label="Featured products (comma-separated)"><Input value={f.featured_products} onChange={(e) => upd("featured_products", e.target.value)} /></AF>
          <ImageUploadField label="Brand logo (shown on the Brands page)" value={f.image} onChange={(value) => upd("image", value)} testid="brand-logo" />
          <AF label="Catalog URL"><Input value={f.catalog_url} onChange={(e) => upd("catalog_url", e.target.value)} /></AF>
        </div>
        <DialogFooter><Button onClick={() => onSave(f)} data-testid="save-brand" className="bg-[#0891b2] text-white">Save</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ============ BRANCHES ============
const emptyBranch = { name_en: "", name_ar: "", emirate: "dubai", address_en: "", address_ar: "",
  phones: [""], whatsapp: "", email: "", working_hours_en: "", working_hours_ar: "",
  description_en: "", description_ar: "", map_url: "", map_embed: "", lat: null, lng: null };

function BranchesPanel() {
  const [items, setItems] = useState([]);
  const [editing, setEditing] = useState(null);
  const [open, setOpen] = useState(false);
  const load = () => api.get("/branches").then((r) => setItems(r.data));
  useEffect(() => { load(); }, []);
  const save = async (data) => {
    try {
      const payload = { ...data, lat: data.lat ? parseFloat(data.lat) : null, lng: data.lng ? parseFloat(data.lng) : null };
      if (data.id) await api.put(`/branches/${data.id}`, payload);
      else await api.post("/branches", payload);
      toast.success("Branch saved"); setOpen(false); load();
    } catch (error) { toast.error(error.response?.data?.message || "Could not save branch"); }
  };
  const del = async (id) => { if (!window.confirm("Delete?")) return; await api.delete(`/branches/${id}`); load(); };

  return (
    <div data-testid="branches-panel">
      <div className="flex justify-end mb-4">
        <Button onClick={() => { setEditing(emptyBranch); setOpen(true); }} data-testid="add-branch-btn" className="bg-[#ef4444] text-white h-10"><Plus className="w-4 h-4 me-2" />Add Branch</Button>
      </div>
      <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
        {items.map((b, i) => (
          <div key={b.id} className="bg-white border border-slate-200 rounded-lg p-4" data-testid={`admin-branch-${i}`}>
            <div className="flex items-start gap-3 mb-4"><div className="w-10 h-10 shrink-0 rounded-lg bg-cyan-50 text-[#0891b2] flex items-center justify-center"><MapPinIcon className="w-5 h-5" /></div><div><h3 className="font-semibold text-slate-900">{b.name_en || b.name}</h3><p className="text-xs text-slate-500 mt-1">{b.address_en || b.address}</p></div></div>
            <div className="space-y-1 text-xs text-slate-600 mb-4"><p className="truncate">{b.email}</p><p dir="ltr">{b.phones?.find(Boolean) || b.phone}</p></div>
            {b.map_embed && <iframe src={b.map_embed} title={`branch-map-${b.id}`} loading="lazy" className="w-full h-28 rounded border border-slate-100 mb-4" />}
            <div className="flex gap-2">
              <button onClick={() => { setEditing({ ...b, name_en: b.name_en || b.name || "", address_en: b.address_en || b.address || "", phones: b.phones?.length ? b.phones : (b.phone ? [b.phone] : [""]) }); setOpen(true); }} data-testid={`edit-branch-${i}`} className="text-xs text-[#0891b2] hover:underline flex items-center gap-1"><Pencil className="w-3 h-3" />Edit</button>
              <button onClick={() => del(b.id)} data-testid={`delete-branch-${i}`} className="text-xs text-slate-500 hover:text-red-600 ms-auto flex items-center gap-1"><Trash2 className="w-3 h-3" />Delete</button>
            </div>
          </div>
        ))}
      </div>
      <BranchDialog open={open} onOpenChange={setOpen} editing={editing} onSave={save} />
    </div>
  );
}

function BranchDialog({ open, onOpenChange, editing, onSave }) {
  const [f, setF] = useState(emptyBranch);
  useEffect(() => { if (editing) setF(editing); }, [editing]);
  const upd = (k, v) => setF((p) => ({ ...p, [k]: v }));
  const updPhone = (idx, v) => { const p = [...f.phones]; p[idx] = v; upd("phones", p); };
  const addPhone = () => upd("phones", [...(f.phones || []), ""]);
  const removePhone = (idx) => upd("phones", f.phones.filter((_, i) => i !== idx));

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader><DialogTitle>{f.id ? "Edit" : "New"} Branch</DialogTitle></DialogHeader>
        <div className="grid sm:grid-cols-2 gap-3">
          <AF label="Name (EN)"><Input value={f.name_en} onChange={(e) => upd("name_en", e.target.value)} /></AF>
          <AF label="Name (AR)"><Input dir="rtl" value={f.name_ar} onChange={(e) => upd("name_ar", e.target.value)} /></AF>
          <AF label="Emirate">
            <Select value={f.emirate} onValueChange={(v) => upd("emirate", v)}>
              <SelectTrigger><SelectValue /></SelectTrigger>
              <SelectContent>{EMIRATES.map((e) => <SelectItem key={e} value={e}>{e}</SelectItem>)}</SelectContent>
            </Select>
          </AF>
          <AF label="Email"><Input value={f.email} onChange={(e) => upd("email", e.target.value)} /></AF>
          <div className="sm:col-span-2"><AF label="Address (EN)"><Textarea rows={2} value={f.address_en} onChange={(e) => upd("address_en", e.target.value)} /></AF></div>
          <div className="sm:col-span-2"><AF label="Address (AR)"><Textarea rows={2} dir="rtl" value={f.address_ar} onChange={(e) => upd("address_ar", e.target.value)} /></AF></div>
          <AF label="Working hours (EN)"><Input value={f.working_hours_en} onChange={(e) => upd("working_hours_en", e.target.value)} /></AF>
          <AF label="Working hours (AR)"><Input dir="rtl" value={f.working_hours_ar} onChange={(e) => upd("working_hours_ar", e.target.value)} /></AF>
          <AF label="WhatsApp"><Input dir="ltr" value={f.whatsapp} onChange={(e) => upd("whatsapp", e.target.value)} placeholder="+971 4-288-2777" /></AF>
          <AF label="Google Maps link"><Input value={f.map_url} onChange={(e) => upd("map_url", e.target.value)} /></AF>
          <div className="sm:col-span-2"><AF label="Google Maps embed URL (shown on the Branches page)"><Textarea rows={3} value={f.map_embed || ""} onChange={(e) => upd("map_embed", e.target.value)} placeholder="Paste the Google Maps Embed URL here" /></AF></div>
          <AF label="Latitude"><Input value={f.lat ?? ""} onChange={(e) => upd("lat", e.target.value)} /></AF>
          <AF label="Longitude"><Input value={f.lng ?? ""} onChange={(e) => upd("lng", e.target.value)} /></AF>
          <div className="sm:col-span-2">
            <Label className="text-xs uppercase tracking-widest text-slate-500 mb-2 block">Phones</Label>
            <div className="space-y-2">
              {(f.phones || []).map((ph, i) => (
                <div key={i} className="flex gap-2">
                  <Input dir="ltr" value={ph} onChange={(e) => updPhone(i, e.target.value)} />
                  <Button type="button" variant="outline" onClick={() => removePhone(i)} className="px-3"><X className="w-4 h-4" /></Button>
                </div>
              ))}
              <Button type="button" variant="outline" onClick={addPhone} className="h-9 text-xs">Add phone</Button>
            </div>
          </div>
          <div className="sm:col-span-2"><AF label="Description (EN)"><Textarea rows={2} value={f.description_en} onChange={(e) => upd("description_en", e.target.value)} /></AF></div>
          <div className="sm:col-span-2"><AF label="Description (AR)"><Textarea rows={2} dir="rtl" value={f.description_ar} onChange={(e) => upd("description_ar", e.target.value)} /></AF></div>
        </div>
        <DialogFooter><Button onClick={() => onSave(f)} data-testid="save-branch" className="bg-[#0891b2] text-white">Save</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ============ BLOG ============
const emptyBlog = { slug: "", title_en: "", title_ar: "", category: "Restaurant Equipment Tips", published_at: new Date().toISOString().slice(0, 10),
  featured_image: "", author: "Mariot Kitchen Team", excerpt_en: "", excerpt_ar: "",
  content_en: "", content_ar: "" };

function BlogPanel() {
  const [items, setItems] = useState([]);
  const [editing, setEditing] = useState(null);
  const [open, setOpen] = useState(false);
  const load = () => api.get("/blog").then((r) => setItems(r.data));
  useEffect(() => { load(); }, []);
  const save = async (data) => {
    try {
      if (data.id) await api.put(`/blog/${data.id}`, data);
      else await api.post("/blog", data);
      toast.success("News post published"); setOpen(false); load();
    } catch (error) { toast.error(error.response?.data?.message || "Could not save news post"); }
  };
  const del = async (id) => { if (!window.confirm("Delete?")) return; await api.delete(`/blog/${id}`); load(); };

  return (
    <div data-testid="blog-panel">
      <div className="flex justify-end mb-4">
        <Button onClick={() => { setEditing(emptyBlog); setOpen(true); }} data-testid="add-blog-btn" className="bg-[#ef4444] text-white h-10"><Plus className="w-4 h-4 me-2" />Add Blog Content</Button>
      </div>
      <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
        {items.map((b, i) => (
          <div key={b.id} className="bg-white border border-slate-200 rounded-lg p-4" data-testid={`admin-blog-${i}`}>
            <img src={b.featured_image} alt="" className="aspect-video object-cover w-full rounded mb-3" />
            <div className="text-[10px] uppercase text-[#ef4444] tracking-widest mb-1">{b.category}</div>
            <h3 className="font-semibold mb-3 line-clamp-2">{b.title_en}</h3>
            <div className="flex gap-2">
              <button onClick={() => { setEditing(b); setOpen(true); }} data-testid={`edit-blog-${i}`} className="text-xs text-[#0891b2] hover:underline flex items-center gap-1"><Pencil className="w-3 h-3" />Edit</button>
              <button onClick={() => del(b.id)} data-testid={`delete-blog-${i}`} className="text-xs text-slate-500 hover:text-red-600 ms-auto flex items-center gap-1"><Trash2 className="w-3 h-3" />Delete</button>
            </div>
          </div>
        ))}
      </div>
      <BlogDialog open={open} onOpenChange={setOpen} editing={editing} onSave={save} />
    </div>
  );
}

function BlogDialog({ open, onOpenChange, editing, onSave }) {
  const [f, setF] = useState(emptyBlog);
  useEffect(() => { if (editing) setF(editing); }, [editing]);
  const upd = (k, v) => setF((p) => ({ ...p, [k]: v }));

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
        <DialogHeader><DialogTitle>{f.id ? "Edit" : "New"} Blog Post</DialogTitle></DialogHeader>
        <div className="grid sm:grid-cols-2 gap-3">
          <AF label="Title (EN)"><Input value={f.title_en} onChange={(e) => upd("title_en", e.target.value)} /></AF>
          <AF label="Title (AR)"><Input dir="rtl" value={f.title_ar} onChange={(e) => upd("title_ar", e.target.value)} /></AF>
          <AF label="Publish date"><Input type="date" value={(f.published_at || "").slice(0, 10)} onChange={(e) => upd("published_at", e.target.value)} /></AF>
          <AF label="Page URL (optional)"><Input value={f.slug || ""} onChange={(e) => upd("slug", e.target.value)} placeholder="Created automatically from the English title" /></AF>
          <AF label="Category"><Input value={f.category} onChange={(e) => upd("category", e.target.value)} /></AF>
          <AF label="Author"><Input value={f.author} onChange={(e) => upd("author", e.target.value)} /></AF>
          <div className="sm:col-span-2"><ImageUploadField label="Featured image" value={f.featured_image} onChange={(value) => upd("featured_image", value)} testid="blog-image" /></div>
          <AF label="Excerpt (EN)"><Textarea rows={2} value={f.excerpt_en} onChange={(e) => upd("excerpt_en", e.target.value)} /></AF>
          <AF label="Excerpt (AR)"><Textarea rows={2} dir="rtl" value={f.excerpt_ar} onChange={(e) => upd("excerpt_ar", e.target.value)} /></AF>
          <div className="sm:col-span-2 mt-2 rounded-lg border border-cyan-100 bg-cyan-50/50 p-4">
            <div className="font-semibold text-slate-800">Article content</div>
            <p className="text-xs text-slate-500 mt-1">Write the full article below. Paragraph breaks will appear on the public blog page after you save.</p>
          </div>
          <div className="sm:col-span-2"><AF label="Article content (EN)"><Textarea rows={12} value={f.content_en} onChange={(e) => upd("content_en", e.target.value)} placeholder="Write the complete English blog article here…" data-testid="blog-content-en" /></AF></div>
          <div className="sm:col-span-2"><AF label="Article content (AR)"><Textarea rows={12} dir="rtl" value={f.content_ar} onChange={(e) => upd("content_ar", e.target.value)} placeholder="اكتب المقالة العربية كاملة هنا…" data-testid="blog-content-ar" /></AF></div>
        </div>
        <DialogFooter><Button onClick={() => onSave(f)} data-testid="save-blog" className="bg-[#0891b2] text-white">Save</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function AF({ label, children }) {
  return (
    <div className="space-y-1">
      <Label className="text-[11px] uppercase text-slate-500 tracking-widest">{label}</Label>
      {children}
    </div>
  );
}

// ============ SETTINGS ============
function SettingsPanel() {
  const [settings, setSettings] = useState({});
  const [modelUrl, setModelUrl] = useState("");
  const [uploading, setUploading] = useState(false);
  const [saving, setSaving] = useState(false);

  const load = () => api.get("/settings").then((r) => {
    setSettings(r.data);
    setModelUrl(r.data.kitchen_model_url || "");
  });

  useEffect(() => { load(); }, []);

  const save = async () => {
    setSaving(true);
    try {
      await api.put("/settings/kitchen_model_url", { key: "kitchen_model_url", value: modelUrl });
      toast.success("Saved");
      load();
    } catch {
      toast.error("Save failed");
    } finally {
      setSaving(false);
    }
  };

  const handleFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!file.name.match(/\.(glb|gltf)$/i)) {
      toast.error("Please upload a .glb or .gltf file");
      return;
    }
    if (file.size > 10 * 1024 * 1024) {
      toast.error("Max 10MB");
      return;
    }
    const fd = new FormData();
    fd.append("file", file);
    setUploading(true);
    try {
      // Note: /api/upload validates MIME. GLB may be application/octet-stream — allow this in dev by re-uploading manually or use direct URL.
      const { data } = await api.post("/upload", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      const publicUrl = mediaUrl(data.path);
      setModelUrl(publicUrl);
      toast.success(`Uploaded — click Save to apply`);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Upload failed — try a direct HTTPS URL");
    } finally {
      setUploading(false);
    }
  };

  return (
    <div data-testid="settings-panel" className="max-w-3xl">
      <div className="bg-white border border-slate-200 rounded-lg p-6 space-y-5">
        <div className="flex items-center gap-3 pb-4 border-b border-slate-100">
          <div className="w-10 h-10 bg-cyan-50 rounded flex items-center justify-center">
            <SettingsIcon className="w-5 h-5 text-[#ef4444]" />
          </div>
          <div>
            <h2 className="font-display text-xl text-[#334155]">Interactive 3D Viewer</h2>
            <p className="text-sm text-slate-500">Configure the 3D kitchen model shown on the /design page.</p>
          </div>
        </div>

        <div className="space-y-2">
          <Label className="text-sm font-medium text-slate-700">
            Kitchen 3D Model URL <span className="text-slate-400 font-normal">(.glb or .gltf)</span>
          </Label>
          <Input
            value={modelUrl}
            onChange={(e) => setModelUrl(e.target.value)}
            placeholder="https://example.com/your-kitchen.glb"
            className="h-11 font-mono text-sm"
            data-testid="settings-model-url"
            dir="ltr"
          />
          <p className="text-xs text-slate-500">
            Paste a direct HTTPS URL to your GLB file, or upload one below.
          </p>
        </div>

        <div className="flex flex-wrap gap-2">
          <Button
            onClick={save}
            disabled={saving || !modelUrl}
            data-testid="settings-save"
            className="bg-[#ef4444] hover:bg-[#dc2626] text-white h-11 px-6"
          >
            {saving ? "Saving..." : "Save"} <Save className="w-4 h-4 ms-2" />
          </Button>
          <label className="inline-flex items-center gap-2 h-11 px-5 bg-white border border-slate-300 hover:border-[#ef4444] rounded cursor-pointer text-sm text-slate-700">
            <Upload className="w-4 h-4" /> {uploading ? "Uploading..." : "Upload GLB"}
            <input type="file" accept=".glb,.gltf" onChange={handleFile} disabled={uploading} className="hidden" data-testid="settings-file-input" />
          </label>
          <a href="/design" target="_blank" rel="noreferrer">
            <Button variant="outline" className="h-11" data-testid="settings-preview">
              <ExternalLink className="w-4 h-4 me-2" /> Preview on Design page
            </Button>
          </a>
        </div>

        <div className="bg-cyan-50 border border-sky-200 rounded p-4 text-sm text-slate-700 space-y-2">
          <p className="font-semibold text-[#0891b2]">Tips for best results</p>
          <ul className="list-disc ms-5 space-y-1 text-slate-600">
            <li>Use compressed <code className="bg-white px-1 rounded text-xs">.glb</code> (binary glTF) — recommended max 10MB.</li>
            <li>Center your model at origin (0,0,0) with proper scale (meters).</li>
            <li>Add PBR materials for a professional look.</li>
            <li>Free CC0 models: <a href="https://polyhaven.com" target="_blank" rel="noreferrer" className="text-[#0891b2] underline">polyhaven.com</a> · <a href="https://sketchfab.com/features/free-3d-models" target="_blank" rel="noreferrer" className="text-[#0891b2] underline">sketchfab.com</a></li>
          </ul>
        </div>

        <div className="text-xs text-slate-500 pt-3 border-t border-slate-100">
          Currently active URL: <span className="font-mono text-[#0891b2] break-all">{settings.kitchen_model_url}</span>
        </div>
      </div>
    </div>
  );
}


// ============ EMERGENCY LOG PANEL ============

const SOURCE_META = {
  whatsapp_full: { label: "WhatsApp (Maintenance page)", color: "bg-green-100 text-green-700", Icon: MessageCircle },
  whatsapp_compact: { label: "WhatsApp (Compact button)", color: "bg-green-100 text-green-700", Icon: MessageCircle },
  whatsapp: { label: "WhatsApp", color: "bg-green-100 text-green-700", Icon: MessageCircle },
  call: { label: "Phone Call", color: "bg-cyan-100 text-cyan-700", Icon: Phone },
  fab: { label: "Floating Button", color: "bg-slate-100 text-slate-700", Icon: Zap },
};

function formatDuration(seconds) {
  if (!seconds || seconds < 0) return "—";
  if (seconds < 60) return `${Math.round(seconds)}s`;
  if (seconds < 3600) return `${Math.round(seconds / 60)}m`;
  const h = Math.floor(seconds / 3600);
  const m = Math.round((seconds % 3600) / 60);
  return `${h}h ${m}m`;
}

function EmergencyLogPanel() {
  const [items, setItems] = useState([]);
  const [stats, setStats] = useState({});
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("all"); // all | unresolved | resolved
  const [responding, setResponding] = useState(null); // click id currently marking
  const [responderName, setResponderName] = useState(localStorage.getItem("mk_admin_email") || "");
  const [respondNotes, setRespondNotes] = useState("");

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/admin/emergency-log");
      setItems(data.items || []);
      setStats(data.stats || {});
    } catch {
      toast.error("Failed to load emergency log");
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); }, []);

  const markResponded = async (id) => {
    try {
      await api.patch(`/admin/emergency-log/${id}/respond`, {
        responded_by: responderName,
        notes: respondNotes,
        resolved: true,
      });
      toast.success("Marked as responded");
      setResponding(null);
      setRespondNotes("");
      load();
    } catch {
      toast.error("Failed to update");
    }
  };

  const del = async (id) => {
    if (!window.confirm("Delete this record?")) return;
    await api.delete(`/admin/emergency-log/${id}`);
    load();
  };

  const filtered = items.filter((c) => {
    if (filter === "unresolved") return !c.resolved;
    if (filter === "resolved") return c.resolved;
    return true;
  });

  if (loading) return <p className="text-slate-500 p-8 text-center">Loading...</p>;

  return (
    <div className="space-y-4" data-testid="emergency-log-panel">
      {/* Stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <StatCard label="Total Dispatches" value={stats.total || 0} color="text-[#0891b2]" testid="em-stat-total" />
        <StatCard label="Today" value={stats.today || 0} color="text-[#ef4444]" testid="em-stat-today" />
        <StatCard label="Unresolved" value={stats.unresolved || 0} color="text-amber-600" testid="em-stat-unresolved" />
        <StatCard label="Avg Response" value={formatDuration(stats.avg_response_seconds)} color="text-green-600" testid="em-stat-avg" />
      </div>

      {/* Filter */}
      <div className="flex flex-wrap gap-2 bg-white p-3 rounded-lg border border-slate-200" data-testid="em-filter">
        {[
          { key: "all", label: `All (${items.length})` },
          { key: "unresolved", label: `Unresolved (${items.filter(i => !i.resolved).length})` },
          { key: "resolved", label: `Resolved (${items.filter(i => i.resolved).length})` },
        ].map((f) => (
          <button
            key={f.key}
            type="button"
            data-testid={`em-filter-${f.key}`}
            onClick={() => setFilter(f.key)}
            className={`text-xs font-medium px-3 py-1.5 rounded-full transition-all ${
              filter === f.key
                ? f.key === "unresolved" ? "bg-amber-100 text-amber-700 ring-2 ring-amber-200"
                : f.key === "resolved" ? "bg-green-100 text-green-700 ring-2 ring-green-200"
                : "bg-cyan-100 text-cyan-700 ring-2 ring-cyan-200"
                : "bg-slate-50 text-slate-500 hover:bg-slate-100"
            }`}
          >
            {f.label}
          </button>
        ))}
      </div>

      {/* Items */}
      {filtered.length === 0 ? (
        <p className="text-slate-500 py-16 text-center bg-white rounded-lg border border-slate-200" data-testid="em-empty">
          No emergency dispatches in this filter yet.
        </p>
      ) : filtered.map((c, i) => {
        const meta = SOURCE_META[c.source] || SOURCE_META.whatsapp;
        const Icon = meta.Icon;
        const createdAt = new Date(c.created_at);
        const respondedAt = c.responded_at ? new Date(c.responded_at) : null;
        const respSeconds = respondedAt ? (respondedAt - createdAt) / 1000 : null;
        return (
          <div key={c.id} data-testid={`em-row-${i}`} className={`bg-white rounded-lg border ${c.resolved ? "border-slate-200" : "border-amber-200 shadow-sm"}`}>
            <div className="p-5 grid grid-cols-1 md:grid-cols-12 gap-4 items-start">
              {/* Icon + Source */}
              <div className="md:col-span-3 flex items-start gap-3">
                <div className={`w-10 h-10 rounded-lg ${meta.color} flex items-center justify-center shrink-0`}>
                  <Icon className="w-4 h-4" />
                </div>
                <div className="min-w-0">
                  <div className="text-sm font-semibold text-slate-800">{meta.label}</div>
                  <div className="text-xs text-slate-500 mt-0.5 truncate" title={c.page}>{c.page || "—"}</div>
                </div>
              </div>

              {/* Timestamps */}
              <div className="md:col-span-4">
                <div className="flex items-center gap-1.5 text-xs text-slate-500 mb-0.5">
                  <Clock className="w-3 h-3" /> Received
                </div>
                <div className="text-sm font-medium text-slate-800" dir="ltr">
                  {createdAt.toLocaleString()}
                </div>
                {respondedAt && (
                  <>
                    <div className="flex items-center gap-1.5 text-xs text-green-600 mt-2 mb-0.5">
                      <CheckCircle2 className="w-3 h-3" /> Responded
                    </div>
                    <div className="text-sm font-medium text-green-700" dir="ltr">
                      {respondedAt.toLocaleString()}
                    </div>
                  </>
                )}
              </div>

              {/* Response time */}
              <div className="md:col-span-2">
                {respSeconds !== null ? (
                  <>
                    <div className="text-[10px] uppercase tracking-widest text-slate-400 font-semibold mb-1">Response Time</div>
                    <div className={`font-display text-2xl ${respSeconds < 300 ? "text-green-600" : respSeconds < 1800 ? "text-amber-600" : "text-[#ef4444]"}`}>
                      {formatDuration(respSeconds)}
                    </div>
                  </>
                ) : (
                  <div className="inline-flex items-center gap-1 bg-amber-100 text-amber-700 rounded-full px-2.5 py-1 text-[10px] uppercase tracking-widest font-semibold">
                    <AlertTriangle className="w-3 h-3" /> Awaiting
                  </div>
                )}
              </div>

              {/* Actions */}
              <div className="md:col-span-3 flex flex-col gap-2 items-end">
                {!c.resolved && (
                  <Button
                    size="sm"
                    onClick={() => setResponding(responding === c.id ? null : c.id)}
                    data-testid={`em-mark-${i}`}
                    className="bg-green-600 hover:bg-green-700 text-white h-9 text-xs w-full"
                  >
                    <CheckCircle2 className="w-3.5 h-3.5 me-1" /> Mark Responded
                  </Button>
                )}
                {c.resolved && c.responded_by && (
                  <div className="text-xs text-slate-500 text-end">
                    by <span className="font-medium text-slate-700">{c.responded_by}</span>
                  </div>
                )}
                <button
                  type="button"
                  onClick={() => del(c.id)}
                  data-testid={`em-del-${i}`}
                  className="text-[11px] text-slate-400 hover:text-[#ef4444] transition-colors"
                >
                  Delete
                </button>
              </div>
            </div>

            {/* Respond form */}
            {responding === c.id && (
              <div className="border-t border-slate-100 bg-slate-50 p-5" data-testid={`em-respond-form-${i}`}>
                <div className="grid sm:grid-cols-2 gap-3 mb-3">
                  <div>
                    <label className="block text-xs text-slate-500 mb-1 font-semibold uppercase tracking-widest">Responded by</label>
                    <Input value={responderName} onChange={(e) => setResponderName(e.target.value)} data-testid={`em-responder-${i}`} className="h-10 bg-white" />
                  </div>
                  <div>
                    <label className="block text-xs text-slate-500 mb-1 font-semibold uppercase tracking-widest">Notes (optional)</label>
                    <Input value={respondNotes} onChange={(e) => setRespondNotes(e.target.value)} data-testid={`em-notes-${i}`} className="h-10 bg-white" placeholder="e.g. Called and dispatched Ahmed" />
                  </div>
                </div>
                <div className="flex gap-2">
                  <Button size="sm" onClick={() => markResponded(c.id)} data-testid={`em-confirm-${i}`} className="bg-green-600 hover:bg-green-700 text-white h-9">
                    Confirm Response
                  </Button>
                  <Button size="sm" variant="outline" onClick={() => setResponding(null)} className="h-9">Cancel</Button>
                </div>
              </div>
            )}

            {c.notes && c.resolved && (
              <div className="border-t border-slate-100 px-5 py-3 text-xs text-slate-600 bg-slate-50">
                <span className="font-semibold text-slate-500 uppercase tracking-widest text-[10px] me-2">Note:</span>
                {c.notes}
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}

function StatCard({ label, value, color, testid }) {
  return (
    <div data-testid={testid} className="bg-white rounded-xl border border-slate-200 p-5">
      <div className={`font-display text-3xl ${color} mb-1`}>{value}</div>
      <div className="text-xs uppercase tracking-widest text-slate-500">{label}</div>
    </div>
  );
}
